import { Actor, ActorSubclass, HttpAgent } from '@dfinity/agent'
import { idlFactory as oneSecActor } from '../idls/onesec/onesec.did'
import type {
  _SERVICE as OneSecActor,
  AssetRequest,
  Asset,
  Token,
  Chain as OneSecChain,
  TransferFee,
} from '../idls/onesec/onesec.did'
import {
  BOB_ARB_ID,
  BOB_BASE_ID,
  BOB_ETH_ID,
  BOB_ICP_ID,
  CBBTC_ARB_ID,
  CBBTC_BASE_ID,
  CBBTC_ETH_ID,
  CBBTC_ICP_ID,
  CKBTC_ARB_ID,
  CKBTC_BASE_ID,
  CKBTC_ETH_ID,
  CKBTC_ICP_ID,
  GLDT_ARB_ID,
  GLDT_BASE_ID,
  GLDT_ETH_ID,
  GLDT_ICP_ID,
  ICP_ARB_ID,
  ICP_BASE_ID,
  ICP_ETH_ID,
  ICP_ICP_ID,
  USDC_ARB_ID,
  USDC_BASE_ID,
  USDC_ETH_ID,
  USDC_ICP_ID,
  USDT_ETH_ID,
  USDT_ICP_ID,
} from '../constants/tokenIds'
import { Principal } from '@dfinity/principal'
import { Chain } from '../interfaces/common'
import { getFirstKey } from '../utils/getFirstKey'

export const ONESEC_MINTER_CANISTER = '5okwm-giaaa-aaaar-qbn6a-cai'

const CHAIN_TO_ONESEC_CHAIN: Partial<Record<Chain, string>> = {
  [Chain.ICP]: 'ICP',
  [Chain.ETH]: 'Ethereum',
  [Chain.ARB]: 'Arbitrum',
  [Chain.BASE]: 'Base',
}

export const ONESEC_CHAIN_TO_CHAIN: Record<string, Chain> = {
  ICP: Chain.ICP,
  Ethereum: Chain.ETH,
  Arbitrum: Chain.ARB,
  Base: Chain.BASE,
}

const ONESEC_TO_PLUG_MAP = {
  [Chain.ICP]: {
    USDC: USDC_ICP_ID,
    USDT: USDT_ICP_ID,
    BOB: BOB_ICP_ID,
    CBBTC: CBBTC_ICP_ID,
    CKBTC: CKBTC_ICP_ID,
    ICP: ICP_ICP_ID,
    GLDT: GLDT_ICP_ID,
  },
  [Chain.ETH]: {
    USDC: USDC_ETH_ID,
    USDT: USDT_ETH_ID,
    BOB: BOB_ETH_ID,
    CBBTC: CBBTC_ETH_ID,
    CKBTC: CKBTC_ETH_ID,
    ICP: ICP_ETH_ID,
    GLDT: GLDT_ETH_ID,
  },
  [Chain.ARB]: {
    USDC: USDC_ARB_ID,
    BOB: BOB_ARB_ID,
    CBBTC: CBBTC_ARB_ID,
    CKBTC: CKBTC_ARB_ID,
    ICP: ICP_ARB_ID,
    GLDT: GLDT_ARB_ID,
  },
  [Chain.BASE]: {
    USDC: USDC_BASE_ID,
    BOB: BOB_BASE_ID,
    CBBTC: CBBTC_BASE_ID,
    CKBTC: CKBTC_BASE_ID,
    ICP: ICP_BASE_ID,
    GLDT: GLDT_BASE_ID,
  },
  [Chain.BTC]: {},
  [Chain.SOL]: {},
  [Chain.ODIN]: {},
}

class OneSecClient {
  public agent: HttpAgent
  private helperActor: ActorSubclass<OneSecActor>

  constructor(agent: HttpAgent) {
    this.agent = agent
    this.helperActor = Actor.createActor(oneSecActor, {
      canisterId: ONESEC_MINTER_CANISTER,
      agent,
    }) as ActorSubclass<OneSecActor>
  }

  static oneSecTokenToPlugToken(token: undefined, chain: string | undefined) {
    if (token && chain) {
      const plugChain = ONESEC_CHAIN_TO_CHAIN[chain]
      if (plugChain) {
        const tokenId = ONESEC_TO_PLUG_MAP[plugChain]?.[token]
        return tokenId
      }
    }
    return undefined
  }

  async fetchFees(
    srcTokenSymbol: string,
    dstTokenSymbol: string,
    srcTokenChain: Chain,
    dstTokenChain: Chain
  ): Promise<TransferFee | undefined> {
    const pairs = await this.helperActor.get_transfer_fees()

    const srcChain = CHAIN_TO_ONESEC_CHAIN[srcTokenChain]!
    const dstChain = CHAIN_TO_ONESEC_CHAIN[dstTokenChain]!

    const pair = pairs.find(
      (p) =>
        getFirstKey(p.source_chain) === srcChain &&
        getFirstKey(p.destination_chain) === dstChain &&
        getFirstKey(p.source_token) === srcTokenSymbol &&
        getFirstKey(p.destination_token) === dstTokenSymbol
    )

    return pair
  }

  async fetchHistory(principalId: string, ethAddress: string) {
    const history = await this.helperActor.get_transfers({
      count: BigInt(100),
      skip: BigInt(0),
      accounts: [
        {
          Icp: {
            ICRC: {
              owner: Principal.fromText(principalId),
              subaccount: [],
            },
          },
        },
        {
          Evm: {
            address: ethAddress,
          },
        },
      ],
    })

    return history
  }

  async bridgeFromICP(
    bridgeAmount: number,
    ethAddr: string,
    principal: string,
    srcTokenSymbol: string,
    dstTokenSymbol: string,
    dstTokenChain: Chain
  ): Promise<bigint> {
    const source: Asset = {
      tx: [],
      token: { [srcTokenSymbol]: null } as Token,
      chain: { ICP: null },
      account: {
        Icp: {
          ICRC: {
            owner: Principal.fromText(principal),
            subaccount: [],
          },
        },
      },
      amount: BigInt(bridgeAmount),
    }

    const dstChain = CHAIN_TO_ONESEC_CHAIN[dstTokenChain]!

    const destination: AssetRequest = {
      token: { [dstTokenSymbol]: null } as Token,
      chain: { [dstChain]: null } as OneSecChain,
      account: {
        Evm: {
          address: ethAddr,
        },
      },
      amount: [],
    }

    const response = await this.helperActor.transfer({
      destination,
      source,
    })

    if ('Failed' in response) {
      throw response.Failed.error
    }

    if ('Fetching' in response) {
      return response.Fetching.block_height
    }

    return response.Accepted.id
  }
}

export default OneSecClient
